import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOptimizedFamily } from "@/hooks/useOptimizedFamily";
import { useToast } from "@/hooks/use-toast";
import { Crown, Check, Loader2, X } from "lucide-react";
import { useInAppPurchase } from "@/hooks/useInAppPurchase";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";

export function Upgrade() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { family } = useOptimizedFamily();
  const { toast } = useToast();
  const returnTo = searchParams.get("returnTo") || "/dashboard";

  // Toggle annuel/mensuel
  const [isAnnual, setIsAnnual] = useState(true);
  const { purchaseProduct, isLoading, products, isInitialized, PRODUCT_IDS } = useInAppPurchase();

  const handleSubscribe = async () => {
    if (!family?.id) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une famille.",
        variant: "destructive",
      });
      return;
    }

    if (!isInitialized) {
      toast({
        title: "Veuillez patienter",
        description: "Le système de paiement se charge...",
        variant: "destructive",
      });
      return;
    }

    const productId = isAnnual ? PRODUCT_IDS.annual : PRODUCT_IDS.monthly;
    const success = await purchaseProduct(productId, family.id);

    if (success) {
      navigate(returnTo);
    }
  };

  const currentProduct = isAnnual 
    ? products.find(p => p.id === PRODUCT_IDS.annual)
    : products.find(p => p.id === PRODUCT_IDS.monthly);

  const price = isAnnual ? "44,99€" : "4,99€";
  const period = isAnnual ? "an" : "mois";
  const displayPrice = currentProduct?.displayPrice || `${price}/${period}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container max-w-2xl mx-auto px-4 py-6">
        {/* Header avec couronne centrée et bouton fermeture */}
        <div className="relative flex items-center justify-center mb-4 mt-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
            <Crown className="h-6 w-6 text-primary" />
          </div>
          <Button variant="ghost" size="icon" onClick={() => navigate(returnTo)} className="absolute right-0">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Passez à Clan Pro</h1>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Débloquez toutes les fonctionnalités pour organiser votre famille comme jamais auparavant.
          </p>
        </div>

        {/* Carte unique avec toggle */}
        <Card className="border-2 border-primary bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader className="text-center space-y-4">
            {/* Toggle Mensuel/Annuel */}
            <div className="flex items-center justify-center gap-3 py-2">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
                Mensuel
              </span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
                className="data-[state=checked]:bg-primary"
              />
              <span className={`text-sm font-medium ${isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
                Annuel
              </span>
            </div>

            {/* Prix */}
            <div>
              <div className="text-4xl font-bold mb-1">
                {price}
                <span className="text-lg font-normal text-muted-foreground">/{period}</span>
              </div>
              {isAnnual && (
                <p className="text-sm text-primary font-medium">
                  Économisez 25% • 2 mois offerts
                </p>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Features */}
            <div className="space-y-2.5">
              <Feature text="Invitations familiales illimitées" />
              <Feature text="Calendriers partagés synchronisés" />
              <Feature text="Listes de tâches collaboratives" />
              <Feature text="Stockage documents sécurisé" />
              <Feature text="Support prioritaire" />
            </div>

            {/* Bouton d'abonnement */}
            <Button
              onClick={handleSubscribe}
              disabled={isLoading || !isInitialized}
              className="w-full mt-5"
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Traitement en cours...
                </>
              ) : (
                `S'abonner • ${displayPrice}`
              )}
            </Button>

            {/* Texte obligatoire App Store */}
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground text-center leading-relaxed">
                L'abonnement se renouvelle automatiquement à {price}/{period} jusqu'à annulation. 
                Gérez vos abonnements dans les réglages de votre compte Apple ou l'App Store.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Liens légaux */}
        <div className="flex items-center justify-center gap-4 mt-6 text-xs text-muted-foreground">
          <Link to="/terms-of-service" className="hover:text-foreground underline">
            Conditions d'utilisation
          </Link>
          <span>•</span>
          <Link to="/privacy-policy" className="hover:text-foreground underline">
            Politique de confidentialité
          </Link>
        </div>

        {/* Bouton "Plus tard" */}
        <div className="text-center mt-6">
          <Button variant="ghost" onClick={() => navigate(returnTo)}>
            Peut-être plus tard
          </Button>
        </div>
      </div>
    </div>
  );
}

// ————— Petits helpers UI —————
function Feature({ text, highlight = false }: { text: string; highlight?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Check className={`h-4 w-4 ${highlight ? "text-primary" : "text-green-600"}`} />
      <span className={`text-sm ${highlight ? "font-medium text-primary" : ""}`}>{text}</span>
    </div>
  );
}
